/**
 * PremiumWheelPicker
 * iOS-style wheel picker component with native scroll-snap
 * 
 * Features:
 * - Native CSS scroll-snap for smooth scrolling
 * - Touch/pointer event support for momentum scroll
 * - Accessibility: keyboard navigation, aria labels, screen reader support
 * - prefers-reduced-motion support
 * - Large touch targets for mobile-first UX
 * - Optimized for Android/iOS WebView
 */

import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react';

const ITEM_HEIGHT = 44; // Large touch target
const VISIBLE_ITEMS = 3;
const CONTAINER_HEIGHT = ITEM_HEIGHT * VISIBLE_ITEMS;
const CENTER_OFFSET = Math.floor(VISIBLE_ITEMS / 2) * ITEM_HEIGHT;

const PremiumWheelPicker = ({ 
  items = [], 
  value, 
  onChange, 
  label,
  ariaLabel,
  disabled = false,
  width = 80,
  testId
}) => {
  const containerRef = useRef(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef(null);
  const isInitializedRef = useRef(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Find current index
  const currentIndex = useMemo(() => {
    return items.findIndex(item => {
      const itemValue = typeof item === 'object' ? item.value : item;
      return itemValue === value;
    });
  }, [items, value]);

  // Get item value helper
  const getItemValue = useCallback((item) => {
    return typeof item === 'object' ? item.value : item;
  }, []);

  // Get item label helper
  const getItemLabel = useCallback((item) => {
    if (typeof item === 'object') {
      return item.label || String(item.value);
    }
    return String(item);
  }, []);

  // Scroll to index
  const scrollToIndex = useCallback((index, smooth = true) => {
    if (!containerRef.current || index < 0 || index >= items.length) return;
    
    const scrollTop = index * ITEM_HEIGHT;
    const behavior = smooth && !prefersReducedMotion ? 'smooth' : 'auto';
    
    containerRef.current.scrollTo({
      top: scrollTop,
      behavior
    });
  }, [items.length, prefersReducedMotion]);

  // Initialize scroll position
  useEffect(() => {
    if (currentIndex >= 0 && !isInitializedRef.current) {
      // Wait for DOM to be ready
      requestAnimationFrame(() => {
        scrollToIndex(currentIndex, false);
        isInitializedRef.current = true;
      });
    }
  }, [currentIndex, scrollToIndex]);

  // Re-scroll when value changes externally
  useEffect(() => {
    if (isInitializedRef.current && currentIndex >= 0 && !isScrolling) {
      scrollToIndex(currentIndex, true);
    }
  }, [value, currentIndex, scrollToIndex, isScrolling]);

  // Handle scroll end - snap to nearest item
  const handleScroll = useCallback(() => {
    if (disabled) return;
    
    setIsScrolling(true);
    
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Debounce scroll end detection
    scrollTimeoutRef.current = setTimeout(() => {
      if (!containerRef.current) return;
      
      const scrollTop = containerRef.current.scrollTop;
      const nearestIndex = Math.round(scrollTop / ITEM_HEIGHT);
      const clampedIndex = Math.max(0, Math.min(nearestIndex, items.length - 1));
      
      // Snap to nearest
      scrollToIndex(clampedIndex, true);
      
      // Update value if changed
      const selectedItem = items[clampedIndex];
      const newValue = getItemValue(selectedItem);
      
      if (newValue !== value) {
        onChange(newValue);
      }
      
      setIsScrolling(false);
    }, 80); // Short delay for responsive feel
  }, [items, value, onChange, scrollToIndex, getItemValue, disabled]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (disabled) return;
    
    let newIndex = currentIndex;
    
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        newIndex = Math.max(0, currentIndex - 1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        newIndex = Math.min(items.length - 1, currentIndex + 1);
        break;
      case 'Home':
        e.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        newIndex = items.length - 1;
        break;
      case 'PageUp':
        e.preventDefault();
        newIndex = Math.max(0, currentIndex - 5);
        break;
      case 'PageDown':
        e.preventDefault();
        newIndex = Math.min(items.length - 1, currentIndex + 5);
        break;
      default:
        return;
    }
    
    if (newIndex !== currentIndex) {
      scrollToIndex(newIndex, true);
      const selectedItem = items[newIndex];
      onChange(getItemValue(selectedItem));
    }
  }, [currentIndex, items, onChange, scrollToIndex, getItemValue, disabled]);

  // Handle item click
  const handleItemClick = useCallback((index) => {
    if (disabled) return;
    
    scrollToIndex(index, true);
    const selectedItem = items[index];
    onChange(getItemValue(selectedItem));
  }, [items, onChange, scrollToIndex, getItemValue, disabled]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const containerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width,
    opacity: disabled ? 0.4 : 1,
    pointerEvents: disabled ? 'none' : 'auto',
    transition: 'opacity 0.2s ease'
  };

  const labelStyles = {
    color: 'rgba(255,255,255,0.5)',
    fontSize: '11px',
    marginBottom: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: '500',
    textAlign: 'center'
  };

  const wrapperStyles = {
    position: 'relative',
    height: CONTAINER_HEIGHT,
    width: '100%',
    overflow: 'hidden',
    borderRadius: '12px',
    background: 'rgba(255,255,255,0.04)'
  };

  const highlightStyles = {
    position: 'absolute',
    top: '50%',
    left: 0,
    right: 0,
    height: ITEM_HEIGHT,
    transform: 'translateY(-50%)',
    background: 'rgba(139,92,246,0.15)',
    borderTop: '1px solid rgba(139,92,246,0.25)',
    borderBottom: '1px solid rgba(139,92,246,0.25)',
    pointerEvents: 'none',
    zIndex: 1
  };

  const gradientTopStyles = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: CENTER_OFFSET,
    background: 'linear-gradient(to bottom, rgba(15,23,42,0.95), rgba(15,23,42,0.7), transparent)',
    pointerEvents: 'none',
    zIndex: 2
  };

  const gradientBottomStyles = {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: CENTER_OFFSET,
    background: 'linear-gradient(to top, rgba(15,23,42,0.95), rgba(15,23,42,0.7), transparent)',
    pointerEvents: 'none',
    zIndex: 2
  };

  const scrollContainerStyles = {
    height: '100%',
    overflowY: 'scroll',
    scrollSnapType: 'y mandatory',
    WebkitOverflowScrolling: 'touch', // iOS momentum scroll
    paddingTop: CENTER_OFFSET,
    paddingBottom: CENTER_OFFSET,
    scrollbarWidth: 'none', // Firefox
    msOverflowStyle: 'none', // IE/Edge
    outline: 'none'
  };

  return (
    <div style={containerStyles}>
      {label && (
        <div style={labelStyles} id={`${testId || 'wheel'}-label`}>
          {label}
        </div>
      )}
      
      <div style={wrapperStyles}>
        {/* Selection highlight bar */}
        <div style={highlightStyles} />
        
        {/* Gradient fade overlays */}
        <div style={gradientTopStyles} />
        <div style={gradientBottomStyles} />
        
        {/* Scrollable list */}
        <div
          ref={containerRef}
          onScroll={handleScroll}
          onKeyDown={handleKeyDown}
          tabIndex={disabled ? -1 : 0}
          role="listbox"
          aria-label={ariaLabel || label || 'Değer seçin'}
          aria-activedescendant={currentIndex >= 0 ? `${testId || 'wheel'}-item-${currentIndex}` : undefined}
          aria-labelledby={label ? `${testId || 'wheel'}-label` : undefined}
          data-testid={testId}
          className="wheel-picker-scroll"
          style={scrollContainerStyles}
        >
          {items.map((item, index) => {
            const itemValue = getItemValue(item);
            const itemLabel = getItemLabel(item);
            const isSelected = itemValue === value;
            
            return (
              <div
                key={`${itemValue}-${index}`}
                id={`${testId || 'wheel'}-item-${index}`}
                role="option"
                aria-selected={isSelected}
                onClick={() => handleItemClick(index)}
                style={{
                  height: ITEM_HEIGHT,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: isSelected ? 'white' : 'rgba(255,255,255,0.4)',
                  fontSize: isSelected ? '18px' : '15px',
                  fontWeight: isSelected ? '600' : '400',
                  cursor: 'pointer',
                  scrollSnapAlign: 'center',
                  transition: prefersReducedMotion ? 'none' : 'color 0.15s, font-size 0.15s, font-weight 0.15s',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  MozUserSelect: 'none',
                  msUserSelect: 'none',
                  WebkitTapHighlightColor: 'transparent',
                  minHeight: ITEM_HEIGHT, // Ensure consistent height
                  padding: '0 4px'
                }}
              >
                {itemLabel}
              </div>
            );
          })}
        </div>
        
        {/* Hide scrollbar CSS */}
        <style>{`
          .wheel-picker-scroll::-webkit-scrollbar {
            display: none;
          }
          .wheel-picker-scroll:focus {
            outline: 2px solid rgba(139,92,246,0.5);
            outline-offset: 2px;
          }
        `}</style>
      </div>
    </div>
  );
};

export default PremiumWheelPicker;
